import { ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { History } from 'services';
import {
  MenuRounded,
  CloseRounded,
  ChevronRightRounded,
  KeyboardBackspaceRounded,
  ListRounded,
  AddRounded,
  PublishRounded,
  CheckRounded,
} from '@material-ui/icons';
import { Button, Grid } from '@material-ui/core';

// Assets
import { ReactComponent as AccmeLogo } from 'globals/images/accme-logo-mobile.svg';
import { ReactComponent as JALogo } from 'globals/images/ja-logo.svg';

// Components
import LogoutContainer from 'components/login/LogoutContainer';
import { OrganizationSelect } from '../../UserControl/OrganizationSelect';
import { ProviderSelect } from '../../UserControl/ProviderSelect';

// Styles
import styles from './mobileheader.module.scss';

// Core + Store
import {
  auth0UserSelector,
  currentOrganizationContextSelector,
  isJASelector,
  organizationKindSelector,
  userProfileSelector,
} from 'store/user/selectors';
import { IAuth0User, IUser, IUserOrganization, OrganizationKind, OrganizationKinds } from 'core/models';

const MobileHeader = (): ReactElement => {
  const organizationKind: OrganizationKind = useSelector(organizationKindSelector)?.organizationKind;
  const isBoardUser: boolean = organizationKind === OrganizationKinds.BOARD;

  const [, setIsClickOutsideDisabled] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [subMenuOpen, setSubMenuOpen] = useState('');
  History.listen(() => {
    setMenuOpen(false);
    setSubMenuOpen('');
  });

  const menuToggle = () => {
    setMenuOpen(!menuOpen);
    setSubMenuOpen('');
  };

  const subMenuToggle = (key) => {
    setSubMenuOpen(key);
  };

  const subMenuBackToggle = () => {
    setSubMenuOpen('');
  };

  // Selectors
  const auth0User: IAuth0User = useSelector(auth0UserSelector);
  const currentOrganization: IUserOrganization = useSelector(currentOrganizationContextSelector);
  const isJa: boolean = useSelector(isJASelector);
  const userProfile: IUser = useSelector(userProfileSelector);

  return (
    <nav className={classNames('mobile-nav', { ja: isJa })}>
      <div className="mobile-header">
        <div className="mobile-menu-control">
          <button title="menu button" type="button" onClick={menuToggle}>
            {menuOpen ? <CloseRounded /> : <MenuRounded />}
          </button>
        </div>
        <div className="mobile-logo">
          <Link to="/">{isJa ? <JALogo /> : <AccmeLogo />}</Link>
        </div>
      </div>
      <div className={classNames('mobile-main', { open: menuOpen })}>
        <ul className="menu" role="navigation">
          <li>
            <Button className="menu-btn" title="menu expand" type="button" component={Link} to="/" onClick={menuToggle}>
              <span>Dashboard</span>
              <ChevronRightRounded />
            </Button>
          </li>
          <li>
            <Button className="menu-btn" title="menu expand" type="button" onClick={() => subMenuToggle('activity')}>
              <span>Activities</span>
              <ChevronRightRounded />
            </Button>
            <CSSTransition in={subMenuOpen === 'activity'} classNames="open" mountOnEnter timeout={300} unmountOnExit>
              <div className="mobile-sub">
                <button className="tertiary" title="previous menu" type="button" onClick={subMenuBackToggle}>
                  <KeyboardBackspaceRounded /> Back
                </button>
                <div className="menu" role="navigation">
                  <div className="menu-title">Activities</div>
                  <ul>
                    <li>
                      <Link to="/activities/summary" onClick={menuToggle}>
                        <ListRounded />
                        Activity Summary
                      </Link>
                    </li>
                    <li>
                      <Link to="/activities/add-activity" onClick={menuToggle}>
                        <AddRounded />
                        Add Activity
                      </Link>
                    </li>
                    <li>
                      <Link to="/activities/batch" onClick={menuToggle}>
                        <PublishRounded />
                        Batch Upload Activities
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </CSSTransition>
          </li>
          <li>
            <Button className="menu-btn" title="menu expand" type="button" onClick={() => subMenuToggle('learner')}>
              <span>Learners</span>
              <ChevronRightRounded />
            </Button>
            <CSSTransition in={subMenuOpen === 'learner'} classNames="open" mountOnEnter timeout={300} unmountOnExit>
              <div className="mobile-sub">
                <button className="tertiary" title="previous menu" type="button" onClick={subMenuBackToggle}>
                  <KeyboardBackspaceRounded /> Back
                </button>
                <div className="menu" role="navigation">
                  <div className="menu-title">Learners</div>
                  <ul>
                    <li>
                      <Link to="/learners/summary" onClick={menuToggle}>
                        <ListRounded />
                        Learner Summary
                      </Link>
                    </li>
                    <li>
                      <Link to="/learners/search" onClick={menuToggle}>
                        <AddRounded />
                        Learner Search
                      </Link>
                    </li>
                    <li>
                      <Link to="/learners/batch" onClick={menuToggle}>
                        <PublishRounded />
                        Batch Upload Learners
                      </Link>
                    </li>
                    <li>
                      <Link to="/learners/validation" onClick={menuToggle}>
                        <CheckRounded />
                        Learner Validation Tool
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </CSSTransition>
          </li>
          <li>
            <Button
              className="menu-btn"
              title="menu expand"
              type="button"
              component={Link}
              to="/reports"
              onClick={menuToggle}
            >
              <span>Reports</span>
              <ChevronRightRounded />
            </Button>
          </li>
        </ul>
        <div className="user">
          {!isBoardUser && <Link to="/">Back to Accreditation Management</Link>}
          <div className="user-info">
            <div>Logged in as {userProfile?.email || auth0User?.email}</div>
            {currentOrganization && <div>{currentOrganization?.organizationName}</div>}
            {currentOrganization && <div>Organization ID: {currentOrganization?.businessId}</div>}
          </div>
          <div className="user-info dropdown-controls">
            <Grid container>
              <Grid className={styles.dropdown} item xs={12}>
                <OrganizationSelect
                  onSelectClose={() => setIsClickOutsideDisabled(false)}
                  onSelectOpen={() => setIsClickOutsideDisabled(true)}
                />
              </Grid>
              <Grid className={styles.dropdown} item xs={12}>
                <ProviderSelect />
              </Grid>
            </Grid>
          </div>
          <Link to="/contact-support">Contact Support</Link>
          <LogoutContainer />
        </div>
      </div>
    </nav>
  );
};

export default MobileHeader;
